<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import clsx from 'clsx'

import BetaEditorSection from '@/components/beta-editor/BetaEditorSection.vue'
import BetaEditorSectionDivider from '@/components/beta-editor/BetaEditorSectionDivider.vue'
import BetaEditorShell from '@/components/beta-editor/BetaEditorShell.vue'
import PageBetaEditorHeader from '@/components/beta-editor/PageBetaEditorHeader.vue'
import BetaEditorPanelNavigation from '@/components/beta-editor/organisms/BetaEditorPanelNavigation.vue'
import BetaConfirmationModal from '@/components/beta-dashboard/organisms/BetaConfirmationModal.vue'
import BetaModalLayer from '@/components/beta-dashboard/organisms/BetaModalLayer.vue'
import BetaPageSettingsDialog from '@/components/beta-dashboard/organisms/BetaPageSettingsDialog.vue'
import MediaManagerModal from '@/components/organisms/MediaManagerModal.vue'
import PresetModal from '@/components/organisms/PresetModal.vue'
import TextAiModal from '@/components/organisms/TextAiModal.vue'
import modal from '@/services/modal'
import { useAuthStore } from '@/stores/auth'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import { useDashboardStore } from '@/stores/dashboard'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorCommentStore } from '@/stores/editor/comment'
import { useEditorPageStore } from '@/stores/editor/page'
import { useLayoutStore } from '@/stores/layout'
import { useModalPresetStore } from '@/stores/modal/presets'
import { useSiteStore } from '@/stores/site'
import { resolveBetaEditorSitePreviewUrl } from '@/utils/editor-beta-preview'
import { betaPanelQuery } from '@/utils/panel-variant'
import type { BetaPage, BetaSettingsSection } from '@/types/beta-dashboard'
import {
    betaSettingsSectionForAdvancedRoute,
    type BetaAdvancedSettingsRouteName,
} from '@/utils/beta-settings-navigation'
import { isExternalLinkPage } from '@/utils/page'

const authStore = useAuthStore()
const betaStore = useEditorBetaStore()
const betaDashboardStore = useBetaDashboardStore()
const commentStore = useEditorCommentStore()
const dashboardStore = useDashboardStore()
const editorPageStore = useEditorPageStore()
const layoutStore = useLayoutStore()
const presetsStore = useModalPresetStore()
const siteStore = useSiteStore()
const router = useRouter()

const page = ref<any>(null)
const pageForSettings = ref<BetaPage | null>(null)

const loadingGetPage = computed(() => editorPageStore.loadingGetPage)
const loadingPorcentGetPage = computed(() => editorPageStore.loadingPorcentGetPage)
const editorDisabled = computed(() => editorPageStore.editorDisabled)
const activePage = computed(() => editorPageStore.activePage)
const currentPageId = computed(() => {
    const routePageId = router.currentRoute.value.params.id
    const pageId = Array.isArray(routePageId) ? routePageId[0] : routePageId
    const parsedPageId = Number(pageId)

    return Number.isFinite(parsedPageId) ? parsedPageId : null
})
const sitePreviewTarget = computed(() => {
    const routePageId = router.currentRoute.value.params.id
    const pageId = activePage.value?.id
        ?? dashboardStore.activePage?.id
        ?? (Array.isArray(routePageId) ? routePageId[0] : routePageId)

    return pageId === null || pageId === undefined || String(pageId).trim() === ''
        ? null
        : pageId
})
const sitePreviewUrl = computed(() => resolveBetaEditorSitePreviewUrl({
    siteUrl: siteStore.url,
    pagePreviewTarget: sitePreviewTarget.value,
}))
const sitePreviewUnavailableReason = computed(() => (
    sitePreviewUrl.value
        ? null
        : 'O dominio principal do site ou o id da pagina não estão disponíveis para montar o preview real.'
))

const loadEditorPage = async (pageId: number) => {
    editorPageStore.reset()
    betaStore.reset()
    betaStore.setPreviewDevice('desktop')
    betaStore.setPreviewSurface('editor')

    await editorPageStore.getPage(pageId)
    dashboardStore.handleClickPage(page)
    page.value = reactive(editorPageStore.activePage)
    await presetsStore.fetchPresets()
    await commentStore.fetchComments()
    await layoutStore.fetchTemplateOrganismsCombinations()
    betaStore.syncGalleryPresetExtras()
    await betaStore.hydrateHomeImportedSections()
}

onBeforeMount(async () => {
    void betaDashboardStore.loadNavigationPages()

    if (currentPageId.value !== null) {
        await loadEditorPage(currentPageId.value)
    }
})

watch(currentPageId, async (nextPageId, previousPageId) => {
    if (
        router.currentRoute.value.name !== 'page.edit'
        || nextPageId === null
        || previousPageId === null
        || nextPageId === previousPageId
    ) {
        return
    }

    await loadEditorPage(nextPageId)
})

onMounted(() => {
    authStore.checkAccessType()
})

onBeforeUnmount(() => {
    betaStore.reset()
})

const onClickBetaBackground = () => {
    if (betaStore.shouldKeepActiveTextSelection()) {
        return
    }

    betaStore.clearSelectedSection()
    betaStore.clearSidebarTarget()
}

const navigateFromEditor = async (location: Parameters<typeof router.push>[0]) => {
    const result = await modal.confirmation(
        'Sair do editor?',
        'Esta ação poderá resultar na perda de dados não salvos.',
        {
            type: 'danger',
            textConfirm: 'Sim, sair',
            textCancel: 'Continuar editando',
        },
    )

    if (!result) return

    await router.push(location)
}

const goDashboard = () => navigateFromEditor({
    name: 'dashboard',
    query: betaPanelQuery(),
})

const browsePages = () => navigateFromEditor({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'content',
    },
})

const browseOpportunities = () => navigateFromEditor({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'opportunities',
    },
})

const browseAffiliates = () => navigateFromEditor({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'affiliates',
    },
})

const browseSubscriptions = () => navigateFromEditor({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'subscriptions',
    },
})

const browseHelp = () => navigateFromEditor({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'help',
    },
})

const selectPage = (selectedPage: BetaPage) => {
    if (selectedPage.id === currentPageId.value) return

    if (selectedPage.listPages == 1) {
        return navigateFromEditor({
            name: 'dashboard',
            query: {
                ...betaPanelQuery(),
                view: 'content',
                page: selectedPage.id,
            },
        })
    }

    return navigateFromEditor({
        name: isExternalLinkPage(selectedPage) ? 'page.external.edit' : 'page.edit',
        params: { id: selectedPage.id },
        query: betaPanelQuery(),
    })
}

const openLayout = () => navigateFromEditor({
    name: 'dashboard',
    query: { ...betaPanelQuery(), view: 'layout' },
})

const openSettings = (section: BetaSettingsSection) => navigateFromEditor({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'settings',
        settings: section,
    },
})

const openAdvancedSettings = (routeName: BetaAdvancedSettingsRouteName) => navigateFromEditor({
    name: 'dashboard',
    query: {
        ...betaPanelQuery(),
        view: 'settings',
        settings: betaSettingsSectionForAdvancedRoute(routeName),
    },
})

const openPageSettings = (selectedPage: BetaPage) => {
    pageForSettings.value = selectedPage
}

const handleSavedPageSettings = (updatedPage: BetaPage) => {
    if (updatedPage.id !== currentPageId.value || !activePage.value) return

    activePage.value.title = updatedPage.name
    activePage.value.name = updatedPage.name
}

const handleDeletedPage = (pageId: number) => {
    if (pageId !== currentPageId.value) return

    router.push({
        name: 'dashboard',
        query: betaPanelQuery(),
    })
}
</script>

<template>
    <BetaModalLayer>
        <media-manager-modal />
        <preset-modal />
        <BetaConfirmationModal />
        <text-ai-modal />
    </BetaModalLayer>
    <BetaPageSettingsDialog
        :page="pageForSettings"
        @close="pageForSettings = null"
        @saved="handleSavedPageSettings"
        @deleted="handleDeletedPage"
    />

    <div class="page-beta-editor-layout">
        <BetaEditorPanelNavigation
            :pages="betaDashboardStore.pages"
            :current-page-id="currentPageId"
            :loading="betaDashboardStore.loadingContent"
            @home="goDashboard"
            @browse-pages="browsePages"
            @opportunities="browseOpportunities"
            @affiliates="browseAffiliates"
            @subscriptions="browseSubscriptions"
            @help="browseHelp"
            @select-page="selectPage"
            @configure-page="openPageSettings"
            @create-page="browsePages"
            @open-layout="openLayout"
            @open-settings="openSettings"
            @open-advanced-settings="openAdvancedSettings"
        />

        <div class="page-beta-editor-layout__main">
            <beta-editor-shell
                id="page-editor-beta"
                :site-preview-url="sitePreviewUrl"
                :site-preview-unavailable-reason="sitePreviewUnavailableReason"
                @background-click="onClickBetaBackground"
            >
                <template #menu>
                    <page-beta-editor-header @back="goDashboard" />
                </template>

                <div class="relative h-auto">
                    <div
                        :class="clsx(
                            loadingGetPage ? 'h-2' : 'h-0',
                            'top-0 bg-[var(--main-color)] transition-all duration-300',
                        )"
                        :style="{ width: `${loadingPorcentGetPage}%` }"
                    />
                </div>

                <template v-if="!loadingGetPage && activePage">
                    <TransitionGroup name="beta-section-flow" tag="div" class="page-beta-sections">
                        <div
                            v-for="(section, sectionIndex) in activePage.content"
                            :key="section.betaClientKey || section.frameId || section.temporaryId || sectionIndex"
                            class="page-beta-section-flow-item"
                        >
                            <beta-editor-section
                                :section="section"
                                :section-index="sectionIndex"
                                :has-delete="activePage.content.length > 1"
                                :is-editor-disabled="editorDisabled"
                                :class="sectionIndex === activePage.content.length - 1 ? 'last-section' : undefined"
                            />

                            <beta-editor-section-divider :insert-index="sectionIndex + 1" />
                        </div>
                    </TransitionGroup>
                </template>
            </beta-editor-shell>
        </div>
    </div>
</template>

<style scoped>
.page-beta-editor-layout {
    --beta-accent: var(--main-color, #2f86f6);
    min-height: 100vh;
}

.page-beta-editor-layout__main {
    min-width: 0;
    padding-left: 255px;
}

.page-beta-sections {
    @apply relative flex flex-col;
}

.page-beta-section-flow-item {
    @apply min-w-0;
}

.beta-section-flow-move,
.beta-section-flow-enter-active,
.beta-section-flow-leave-active {
    transition:
        transform 1000ms ease-in-out,
        opacity 1000ms ease-in-out,
        filter 1000ms ease-in-out;
}

.beta-section-flow-enter-from,
.beta-section-flow-leave-to {
    opacity: 0;
    filter: blur(1px);
    transform: translateY(18px) scale(0.985);
}

.beta-section-flow-leave-active {
    position: absolute;
    width: 100%;
    pointer-events: none;
}

@media (max-width: 1023px) {
    .page-beta-editor-layout__main {
        padding-left: 0;
    }
}
</style>
